import React, { useEffect, useRef, useState } from "react";

/* Tiny inline icons (no external deps) */
function IconMenu(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" {...props}>
      <path fill="currentColor" d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
    </svg>
  );
}
function IconX(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" {...props}>
      <path fill="currentColor" d="M18.3 5.71L12 12.01l-6.29-6.3-1.42 1.42 6.3 6.29-6.3 6.29 1.42 1.42 6.29-6.3 6.29 6.3 1.42-1.42-6.3-6.29 6.3-6.29z" />
    </svg>
  );
}

/* Unified button style */
const btnSolid =
  "inline-flex items-center justify-center rounded-xl px-6 py-3 bg-[#760000] text-white font-semibold hover:brightness-110 transition shadow-[0_0_0_1px_rgba(255,255,255,0.08)]";

const KICKSTARTER_URL =
  "https://www.kickstarter.com/projects/crimemythlabs/crime-myth-labs-the-enigma-of-the-fallen-titan?ref=project_build";

function SectionWithCover({
  id,
  className = "",
  children,
  bgSrc,
  bgSizeClass = "bg-cover",      // ← new: control zoom (cover/contain/percent)
  bgPositionClass = "bg-center", // ← optional: control the focal point
}) {
  return (
    <section id={id} className={`relative isolate ${className}`}>
      {bgSrc && (
        <>
          <div
            aria-hidden
            className={`absolute inset-0 ${bgPositionClass} ${bgSizeClass} bg-no-repeat z-0 pointer-events-none`}
            style={{ backgroundImage: `url(${bgSrc})` }}
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-black/50 z-10 pointer-events-none"
          />
        </>
      )}
      <div className="relative z-20">{children}</div>
    </section>
  );
}

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const videoRef = useRef(null);
  const [hoveredTier, setHoveredTier] = useState(null);


  /* Header shadow/scrolled state */
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Lock body scroll when mobile menu is open */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
  }, [mobileOpen]);

  /* Nudge autoplay on Safari/iOS */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => {
      const p = v.play?.();
      if (p?.catch) p.catch(() => {});
    };
    v.addEventListener("canplay", tryPlay);
    tryPlay();
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);

  return (
    <div className="min-h-screen text-white font-sans bg-black">
      {/* Header */}
      <header
        className={[
          "sticky top-0 z-50 transition-all duration-300 border-b border-white/10",
          isScrolled
            ? "bg-black/90 backdrop-blur-lg shadow-lg shadow-[#760000]/20"
            : "bg-black/70 backdrop-blur"
        ].join(" ")}
      >
        <div className="max-w-7xl mx-auto py-4 md:py-5 px-6 flex items-center justify-between">
          <a href="#cover" className="flex items-center hover:opacity-90 transition">
            {/* Header logo only */}
            <img
              src="/logo.JPG"
              alt="Crime Myth Labs"
              className="h-14 md:h-16 max-h-16 w-auto object-contain shrink-0"
              loading="eager"
              decoding="async"
            />
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm" aria-label="Primary">
            <a href="#cover" className="hover:text-[#ff9b9b] transition">Home</a>
            <a href="#what" className="hover:text-[#ff9b9b] transition">What is the Game</a>
            <a href="#launch" className="hover:text-[#ff9b9b] transition">Launch</a>
            <a href="#how" className="hover:text-[#ff9b9b] transition">How to Play</a>
            <a href="#faq" className="hover:text-[#ff9b9b] transition">FAQ</a>
            <a href="#about" className="hover:text-[#ff9b9b] transition">About</a>
            <a href="#contact" className="hover:text-[#ff9b9b] transition">Contact</a>
          </nav>

          {/* Right: CTA + mobile trigger */}
          <div className="flex items-center gap-3">
            <a href="#launch" className={`${btnSolid} hidden sm:inline-flex`}>Sign up</a>

            <button
              className="md:hidden inline-flex items-center justify-center rounded-xl p-2 border border-white/20 hover:border-white/40"
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
            >
              <IconMenu />
            </button>
          </div>
        </div>

        {/* Mobile slide-over */}
        <div
          className={["fixed inset-0 z-50 md:hidden", mobileOpen ? "pointer-events-auto" : "pointer-events-none"].join(" ")}
          aria-hidden={!mobileOpen}
        >
          <div
            className={["absolute inset-0 bg-black/60 transition-opacity", mobileOpen ? "opacity-100" : "opacity-0"].join(" ")}
            onClick={() => setMobileOpen(false)}
          />
          <aside
            className={[
              "absolute right-0 top-0 h-full w-[280px] bg-neutral-950 border-l border-white/10 p-6",
              "transition-transform duration-300 will-change-transform",
              mobileOpen ? "translate-x-0" : "translate-x-full"
            ].join(" ")}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
            <div className="flex items-center justify-between">
              <span className="font-semibold">Menu</span>
              <button
                className="rounded-lg p-2 border border-white/20 hover:border-white/40"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
              >
                <IconX />
              </button>
            </div>
            <nav className="flex flex-col gap-5 mt-8 text-base font-medium" aria-label="Mobile">
              {[
                ["Home", "#cover"],
                ["What is the Game", "#what"],
                ["Launch", "#launch"],
                ["How to Play", "#how"],
                ["FAQ", "#faq"],
                ["About", "#about"],
                ["Contact", "#contact"]
              ].map(([label, href]) => (
                <a
                  key={href}
                  href={href}
                  className="hover:text-[#ff9b9b] transition"
                  onClick={() => setMobileOpen(false)}
                >
                  {label}
                </a>
              ))}
            </nav>
          </aside>
        </div>
      </header>

     {/* KICKSTARTER SECTION */}
<section
  id="kickstarter"
  className="w-full border-t border-b border-white/10 bg-gradient-to-r from-black via-black to-[#240000] py-10 md:py-14 px-6"
>
  <div className="max-w-6xl mx-auto space-y-8">
    {/* Top: heading + CTAs */}
    <div className="text-center space-y-4">
      <p className="inline-flex items-center gap-2 rounded-full border border-[#05ce78]/60 bg-black/70 px-4 py-1 text-xs font-semibold tracking-wide uppercase text-[#05ce78]">
        Now live on Kickstarter
      </p>

      <h2 className="text-3xl md:text-4xl font-bold">
        Back{" "}
        <span className="text-[#760000]">
          Enigma Of The Fallen Titan
        </span>{" "}
        on <span className="text-[#05ce78]">Kickstarter</span>
      </h2>

      <p className="text-white/80 max-w-2xl mx-auto">
        Help us bring this mythic murder mystery case to life.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
        {/* MAIN CTA – point this to your Kickstarter URL when ready */}
        <a
          href="https://www.kickstarter.com/projects/crimemythlabs/crime-myth-labs-the-enigma-of-the-fallen-titan?ref=project_build"
          target="_blank"
          rel="noreferrer"
          className={btnSolid}
        >
          Now live on Kickstarter - click to launch
        </a>

        <span className="text-xs text-white/60 pt-1 sm:pt-0">
          Limited early-bird discounts · Exclusive first-print rewards
        </span>
      </div>
    </div>

    {/* Tiers list */}
    <div className="space-y-4">
      <h3 className="text-2xl font-semibold text-center">Pledge tiers</h3>
      <p className="text-center text-white/70 text-sm max-w-2xl mx-auto">
        Choose your rank in the investigation. Each tier unlocks different ways to
        explore the case and support future mysteries.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-4">
        {/* Chief Inspector */}
        <a
          href="https://www.kickstarter.com/"
          target="_blank"
          rel="noreferrer"
          className="relative block group"
        >
          {/* Hover image */}
          <div
            className="
              pointer-events-none
              absolute -top-28 left-1/2 -translate-x-1/2
              opacity-0 scale-90
              group-hover:opacity-100 group-hover:scale-100 group-hover:-translate-y-1
              transition duration-200 ease-out
              z-30
            "
          >
            <img
              src="/bundles/chief-inspector.png"
              alt="Chief Inspector tier contents"
              className="w-40 h-auto rounded-xl border border-[#760000] shadow-[0_0_20px_rgba(118,0,0,0.8)]"
            />
          </div>

          <GoldBox className="space-y-2 cursor-pointer transition transform group-hover:-translate-y-1 group-hover:shadow-[0_0_30px_rgba(118,0,0,0.8)] group-hover:border-[#ffb3b3]">
            <p className="text-xs uppercase tracking-wide text-white/60">Digital</p>
            <h4 className="text-lg font-semibold">Chief Inspector</h4>
            <p className="text-sm text-[#05ce78] font-semibold">Free online edition - €50</p>
            <p className="text-sm text-white/70">
              Get a free access code to the full online version of{" "}
              <strong>Enigma Of The Fallen Titan</strong>. Play the entire investigation in your
              browser, solo or with friends, and step into Olympus without paying a drachma.
            </p>
          </GoldBox>
        </a>

        {/* Divinity Liaison Officer */}
        <a
          href="https://www.kickstarter.com/projects/crimemythlabs/crime-myth-labs-the-enigma-of-the-fallen-titan?ref=project_build"
          target="_blank"
          rel="noreferrer"
          className="relative block group"
        >
          {/* Hover image */}
          <div
            className="
              pointer-events-none
              absolute -top-28 left-1/2 -translate-x-1/2
              opacity-0 scale-90
              group-hover:opacity-100 group-hover:scale-100 group-hover:-translate-y-1
              transition duration-200 ease-out
              z-30
            "
          >
            <img
              src="/bundles/divinity-liaison-officer.png"
              alt="Divinity Liaison Officer tier contents"
              className="w-40 h-auto rounded-xl border border-[#760000] shadow-[0_0_20px_rgba(118,0,0,0.8)]"
            />
          </div>

          <GoldBox
            className="
              space-y-2 cursor-pointer transition transform
              group-hover:-translate-y-1
              group-hover:shadow-[0_0_30px_rgba(118,0,0,0.8)]
              group-hover:border-[#ffb3b3]
              border-2 border-[#ffb3b3]
              shadow-[0_0_25px_rgba(255,155,155,0.3)]
            "
          >
            <p className="text-xs uppercase tracking-wide text-[#ffb3b3]">DISCOUNTED</p>
            <h4 className="text-lg font-semibold">Divinity Liaison Officer</h4>
            <p className="text-sm text-[#05ce78] font-semibold">50% off - €35</p>
            <p className="text-sm text-white/70">
              Get the full case at half price, with your choice of physical or online edition.
              Ideal if you want the complete experience and a big discount for backing early.
            </p>
          </GoldBox>
        </a>

        {/* Field Investigator  */}
        <a
          href="https://www.{KICKSTARTER_URL}/"
          target="_blank"
          rel="noreferrer"
          className="relative block group"
        >
          {/* Hover image */}
          <div
            className="
              pointer-events-none
              absolute -top-28 left-1/2 -translate-x-1/2
              opacity-0 scale-90
              group-hover:opacity-100 group-hover:scale-100 group-hover:-translate-y-1
              transition duration-200 ease-out
              z-30
            "
          >
            <img
              src="/bundles/field-investigator.png"
              alt="Field Investigator tier contents"
              className="w-40 h-auto rounded-xl border border-[#760000] shadow-[0_0_20px_rgba(118,0,0,0.8)]"
            />
          </div>

          <GoldBox className="space-y-2 cursor-pointer transition transform group-hover:-translate-y-1 group-hover:shadow-[0_0_30px_rgba(118,0,0,0.8)] group-hover:border-[#ffb3b3]">
            <p className="text-xs uppercase tracking-wide text-white/60">Discounted</p>
            <h4 className="text-lg font-semibold">Field Investigator</h4>
            <p className="text-sm text-[#05ce78] font-semibold">35% off - €25</p>
            <p className="text-sm text-white/70">
              This tier gives you either the full physical or online edition of{" "}
              <strong>Enigma Of The Fallen Titan</strong> at 35% off the future retail price.
              Choose how you want to investigate, at the table or entirely online.
            </p>
          </GoldBox>
        </a>

        {/* Relic Analyst */}
        <a
          href="https://www.{KICKSTARTER_URL}/"
          target="_blank"
          rel="noreferrer"
          className="relative block group"
        >
          {/* Hover image */}
          <div
            className="
              pointer-events-none
              absolute -top-28 left-1/2 -translate-x-1/2
              opacity-0 scale-90
              group-hover:opacity-100 group-hover:scale-100 group-hover:-translate-y-1
              transition duration-200 ease-out
              z-30
            "
          >
            <img
              src="/bundles/relic-analyst.png"
              alt="Relic Analyst tier contents"
              className="w-40 h-auto rounded-xl border border-[#760000] shadow-[0_0_20px_rgba(118,0,0,0.8)]"
            />
          </div>

          <GoldBox className="space-y-2 cursor-pointer transition transform group-hover:-translate-y-1 group-hover:shadow-[0_0_30px_rgba(118,0,0,0.8)] group-hover:border-[#ffb3b3]">
            <p className="text-xs uppercase tracking-wide text-white/60">Discounted</p>
            <h4 className="text-lg font-semibold">Relic Analyst</h4>
            <p className="text-sm text-[#05ce78] font-semibold">20% off - €15</p>
            <p className="text-sm text-white/70">
              A more accessible way to secure the physical or online edition with a solid 20%
              discount. Great if you want to back the project and still save on the full case.
            </p>
          </GoldBox>
        </a>

        {/* Junior Examiner */}
        <a
          href="https://www.{KICKSTARTER_URL}/"
          target="_blank"
          rel="noreferrer"
          className="relative block group"
        >
          {/* Hover image */}
          <div
            className="
              pointer-events-none
              absolute -top-28 left-1/2 -translate-x-1/2
              opacity-0 scale-90
              group-hover:opacity-100 group-hover:scale-100 group-hover:-translate-y-1
              transition duration-200 ease-out
              z-30
            "
          >
            <img
              src="/bundles/junior-examiner.png"
              alt="Junior Examiner tier contents"
              className="w-40 h-auto rounded-xl border border-[#760000] shadow-[0_0_20px_rgba(118,0,0,0.8)]"
            />
          </div>

          <GoldBox className="space-y-2 cursor-pointer transition transform group-hover:-translate-y-1 group-hover:shadow-[0_0_30px_rgba(118,0,0,0.8)] group-hover:border-[#ffb3b3]">
            <p className="text-xs uppercase tracking-wide text-white/60">Supporter</p>
            <h4 className="text-lg font-semibold">Junior Examiner</h4>
            <p className="text-sm text-[#05ce78] font-semibold">Thank-you tier - €5</p>
            <p className="text-sm text-white/70">
              A pure support tier for those who want to see Crime Myth Labs exist in the world.
              Your name will be thanked as part of the investigation team.
            </p>
          </GoldBox>
        </a>

        {/* Grand Commissioner */}
        <a
          href="https://www.{KICKSTARTER_URL}/"
          target="_blank"
          rel="noreferrer"
          className="relative block group"
        >
          {/* Hover image */}
          <div
            className="
              pointer-events-none
              absolute -top-28 left-1/2 -translate-x-1/2
              opacity-0 scale-90
              group-hover:opacity-100 group-hover:scale-100 group-hover:-translate-y-1
              transition duration-200 ease-out
              z-30
            "
          >
            <img
              src="/bundles/grand-commissioner.png"  // add this image
              alt="Grand Commissioner tier contents"
              className="w-40 h-auto rounded-xl border border-[#760000] shadow-[0_0_20px_rgba(118,0,0,0.8)]"
            />
          </div>

          <GoldBox className="space-y-2 cursor-pointer transition transform group-hover:-translate-y-1 group-hover:shadow-[0_0_30px_rgba(118,0,0,0.8)] group-hover:border-[#ffb3b3] border border-[#ffb3b3]">
            <p className="text-xs uppercase tracking-wide text-[#ffb3b3]">Premium Hybrid</p>
            <h4 className="text-lg font-semibold">Grand Commissioner</h4>
            <p className="text-sm text-[#05ce78] font-semibold">Hybrid Edition - €100</p>
            <p className="text-sm text-white/70">
              Receive the full physical board game plus two online access codes for{" "}
              <strong>Enigma Of The Fallen Titan</strong>.
            </p>
          </GoldBox>
        </a>
      </div>
    </div>

    <p className="text-center text-xs text-white/60 max-w-3xl mx-auto">
      All details and final reward structures are available on the Kickstarter page.
    </p>
  </div>
</section>


      {/* Main */}
      <main className="w-full">
        {/* HERO with background video (content pinned to bottom) */}
        <section
          id="cover"
          className="relative w-full min-h-[90svh] flex items-center justify-center overflow-hidden"
        >
          {/* Video layer */}
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="/cover1.jpg"
            className="absolute inset-0 z-0 w-full h-full object-cover"
            onCanPlay={(e) => {
              const p = e.currentTarget.play?.();
              if (p?.catch) p.catch(() => {});
            }}
            onError={(e) => console.warn("Hero video failed:", e)}
          >
            <source src="/hero-video.mp4" type="video/mp4" />
          </video>

          {/* Overlay ABOVE the video, but non-interactive */}
          <div
            aria-hidden
            className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-b from-black/40 via-black/60 to-black"
          />

          {/* Bottom-aligned content */}
          <div className="absolute inset-x-0 bottom-0 z-20">
            <div className="mx-auto max-w-5xl px-6 text-center w-full pb-[max(2.5rem,env(safe-area-inset-bottom))]">
              <h1 className="sr-only">Crime Myth Labs</h1>

              <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-4">
                Step into Olympus and become investigators of the Gods. Piece together divine alibis, and expose the truth behind the fallen titan in a cinematic experience.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="#launch" className={btnSolid}>Sign up for launch</a>
                <a href="#how" className={btnSolid}>How to play</a>
              </div>
            </div>
          </div>
        </section>

{/* ---- Our First Case section ---- */}
<SectionWithCover className="py-14 md:py-16 px-6">
  <div className="max-w-6xl mx-auto space-y-12">
    {/* Centered section title */}
    <h2 className="text-3xl md:text-4xl font-bold text-center">
      Our First Case
    </h2>

    {/* Row 1: Case image + Enigma + suspects under Enigma */}
    <div
      id="case"
      className="grid gap-8 md:grid-cols-2 items-start"
    >
      {/* Left: big case image */}
      <div className="flex justify-center">
        <img
          src="/case.jpeg"
          alt="Enigma Of The Fallen Titan case artwork"
          className="w-full max-w-xl h-auto object-contain rounded-2xl border border-[#760000]"
        />
      </div>

      {/* Right: Enigma text + suspects image underneath */}
      <div className="space-y-6">
        <GoldBox>
          <h3 className="text-2xl font-semibold">Enigma Of The Fallen Titan</h3>
          <p className="mt-2">
            A premium murder mystery kit steeped in Greek myth. Examine artifacts, decode ciphers,
            and interrogate the pantheon to uncover the truth.
          </p>
          <ul className="mt-4 space-y-2 list-disc list-inside">
            <li>Sealed envelopes with story-driven reveals</li>
            <li>Access to evidence, digital testimonies and sacred clues</li>
            <li>Spoiler-safe hints &amp; solution site</li>
          </ul>
        </GoldBox>

        {/* Suspects image directly under the Enigma section */}
        <img
          src="/suspects.png"
          alt="Suspects involved in the case"
          className="w-full h-auto object-contain rounded-2xl border border-[#760000]"
        />
      </div>
    </div>

    {/* Row 2: Location map (left) + What Is the Game (right) */}
    <div
      id="what"
      className="grid gap-8 md:grid-cols-2 items-start"
    >
      {/* Left: location map */}
      <div className="flex justify-center">
        <img
          src="/locations-map.png"  // change if needed
          alt="Location map for the case"
          className="w-full max-w-xl h-auto object-contain rounded-2xl border border-[#760000]"
        />
      </div>

      {/* Right: What Is the Game section */}
      <div className="space-y-4">
        <h2 className="text-2xl md:text-3xl font-bold text-center md:text-left">
          What Is the Game?
        </h2>
        <GoldBox>
          <p>
            <strong>The Enigma Of The Fallen Titan</strong> is an escape room style board game
            inspired by Greek mythology. Take the role of divine investigators and unravel a
            crime committed by the Gods themselves. Use evidence, testimonies, and solve a variety
            of challenges to accuse the Guilty One.
          </p>
          <p className="italic mt-3">
            “The gods lie. The dead do not. Unravel the Enigma.”
          </p>
        </GoldBox>
      </div>
    </div>

    {/* Key Features: centered below everything */}
    <div className="max-w-xl mx-auto text-center">
      <h3 className="text-2xl font-semibold mt-4 mb-3">Key Features</h3>
      <div className="space-y-3">
        <GoldBox className="text-center">
          <strong>1–6 players</strong>
        </GoldBox>
        <GoldBox className="text-center">
          <strong>Playtime:</strong> ~90–120 minutes
        </GoldBox>
        <GoldBox className="text-center">
          <strong>One-time, story-driven experience</strong>
        </GoldBox>
      </div>
    </div>
  </div>
</SectionWithCover>





        {/* ---- The rest: NO cover background, plain sections ---- */}
        <section id="launch" className="py-14 md:py-16 px-6 border-t border-white/10">
          <div className="max-w-md mx-auto text-center">
            <h2 className="text-3xl font-bold">Be the First to Investigate</h2>
            <p>This divine mystery is launching soon. Sign up now to get early access and exclusive launch updates.</p>
            <div className="mt-4">
              <div className="ml-embedded" data-form="O7TKQb"></div>
              <p className="text-white/70 text-xs mt-2">
                You can unsubscribe anytime. Read the{" "}
                <a href="/privacy" className="underline underline-offset-4 hover:no-underline">Privacy Policy</a>.
              </p>
            </div>
          </div>
        </section>

        <section id="how" className="py-14 md:py-16 px-6 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">How to Play</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <GoldBox><h4 className="font-semibold">🩸 Unbox the Mystery</h4><p className="text-sm mt-1">Evidence, testimonies, and sacred clues.</p></GoldBox>
              <GoldBox><h4 className="font-semibold">🩸 Digital Archives</h4><p className="text-sm mt-1">Access digital files, testimonies, and images.</p></GoldBox>
              <GoldBox><h4 className="font-semibold">🩸 Solve Riddles, Challenges And More</h4><p className="text-sm mt-1">Uncover contradictions and follow divine leads.</p></GoldBox>
            </div>
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <GoldBox><h4 className="font-semibold">🩸 Reveal the Betrayer</h4><p className="text-sm mt-1">Which god committed the crime?</p></GoldBox>
              <GoldBox><h4 className="font-semibold">🩸 Unearth the Weapon</h4><p className="text-sm mt-1">What relic or method was used?</p></GoldBox>
              <GoldBox><h4 className="font-semibold">🩸 Unravel the Motive</h4><p className="text-sm mt-1">Why was the deed done? Vengeance, jealousy, or justice?</p></GoldBox>
            </div>
          </div>
        </section>

        <section id="highlights" className="py-14 md:py-16 px-6 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Game Highlights</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <GoldBox>Voice-acted testimonies from the gods</GoldBox>
              <GoldBox>Riddles and logic-based problem solving</GoldBox>
              <GoldBox>A one-time, cinematic escape experience</GoldBox>
              <GoldBox className="sm:col-span-2 lg:col-span-1">Built-in system to offer hints and reveals when needed</GoldBox>
              <GoldBox>Story Envelopes: Open in order for dramatic reveals and plot twists.</GoldBox>
            </div>
          </div>
        </section>

        <section id="faq" className="py-14 md:py-16 px-6 border-t border-white/10">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {[
              ["Is this a one-time-use game?", "Yes, like a live escape room, it’s meant to be played once for maximum impact."],
              ["Can I play solo?", "Yes! It’s designed for 1–6 players, and solo play works great."],
              ["Do I need internet to play?", "Yes, some evidence and testimonies are only available online."],
              ["How hard are the challenges?", "The game includes challenges of various difficulties, from approachable logic to deeper riddles. A hint and reveal system is always available, if you get stuck."],
              ["Do I need a phone or a laptop to play?", "Yes, either a phone or a laptop will be necessary."],
              ["Will I be able to pause the game and resume at a later date?", "Yes, you can resume the game at any time."]
            ].map(([q, a]) => (
              <details className="group p-6 bg-black/80 border border-[#760000] rounded-2xl" key={q}>
                <summary className="flex cursor-pointer items-center justify-between text-left text-lg font-semibold list-none">
                  {q}<span className="ml-4 text-white/50 group-open:rotate-180 transition">⌄</span>
                </summary>
                <p className="mt-3 text-white/80">{a}</p>
              </details>
            ))}
          </div>
        </section>

        <section id="about" className="py-14 md:py-16 px-6 border-t border-white/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">About Crime Myth Labs</h2>
            <p className="mb-6">Where Greek Myth meets mystery. Made for curious minds.</p>
            <GoldBox className="max-w-3xl  mx-auto space-y-3">
              <p className="mt-4">
                At Crime Myth Labs, we create stories that fuse mystery and mythology. We design immersive worlds inspired by Greek myths, where every clue reveals a missing puzzle piece and brings you closer to uncovering the truth. Our cases invite you to step into a story, follow the evidence, and bring ancient secrets back to light.
              </p>
            </GoldBox>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-16 text-center px-6 border-t border-white/10">
          <div className="max-w-md mx-auto">
            <h3 className="text-3xl font-bold mb-6">Contact &amp; Social Links</h3>
            <GoldBox className="space-y-2">
              <p>
                <a href="mailto:support@testgame.com" className="underline underline-offset-4 hover:no-underline">
                  contact@crimemythlabs.com
                </a>
              </p>
              <p>                 
        Instagram{" "}
        <a
          href="https://instagram.com/CrimeMythLabs"
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-4 hover:no-underline"
        >
          @CrimeMythLabs
        </a>
      </p>

      <p>
        TikTok{" "}
        <a
          href="https://www.tiktok.com/@CrimeMythLabs"
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-4 hover:no-underline"
        >
          @CrimeMythLabs
        </a>
      </p>
    </GoldBox>
  </div>
</section>

        {/* Footer */}
        <footer className="py-8 border-t border-white/10 text-center text-sm text-white/60 bg-black">
          <p>&copy; {new Date().getFullYear()} Crime Myth Labs. All rights reserved.</p>
        </footer>
      </main>
    </div>
  );
}

/* Reusable gold-outlined box */
function GoldBox({ children, className = "" }) {
  return (
    <div className={`p-6 rounded-2xl bg-black/80 border border-[#760000] text-white/80 backdrop-blur ${className}`}>
      {children}
    </div>
  );
}
